import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";

import { HomeNav } from "@/components/home-nav";
import { SiteFooter } from "@/components/site-footer";
import { getJourney, journeyProjects } from "@/data/journeys";
import { useI18n } from "@/lib/i18n";

export const Route = createFileRoute("/surface")({
  head: () => ({
    meta: [
      { property: "og:url", content: "https://www.luizajesusdoprado.com/surface" },
      { title: "Surface — Photography & Painting | Luiza Jesus do Prado" },
      {
        name: "description",
        content:
          "Photography, mixed media and painting by Luiza Jesus do Prado — expanded image, memory and decoding.",
      },
      {
        property: "og:title",
        content: "Surface — Photography & Painting | Luiza Jesus do Prado",
      },
      { property: "og:description", content: "Photography, mixed media and painting works." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: Surface,
});

function Surface() {
  const { lang } = useI18n();
  const journey = getJourney("surface");
  const items = journeyProjects("surface");
  const [hover, setHover] = useState<number | null>(null);

  return (
    <div className="min-h-screen bg-ink text-bone">
      <HomeNav />

      {/* hover-revealed backdrop */}
      <div className="pointer-events-none fixed inset-0 z-0">
        {items.map((p, i) => (
          <img
            key={p.slug}
            src={p.cover}
            alt=""
            aria-hidden
            className={`absolute inset-0 h-full w-full object-cover contrast-125 transition-opacity duration-700 ${
              hover === i ? "opacity-45" : "opacity-0"
            }`}
          />
        ))}
        <div className="absolute inset-0 bg-gradient-to-b from-ink/70 via-ink/40 to-ink/85" />
      </div>

      <main id="main-content" className="relative z-10 flex min-h-[100svh] flex-col justify-center px-6 pb-24 pt-32 md:px-12">
        <header className="flex items-baseline justify-between border-b border-bone/20 pb-4">
          <h1 className="font-mono text-[10px] uppercase tracking-[0.4em] text-bone/70">
            {journey.label[lang]}
          </h1>
          <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-bone/65">
            {journey.description[lang]}
          </span>
        </header>

        <ul className="mt-10">
          {items.map((p, i) => (
            <li key={p.slug} className="border-b border-bone/12">
              <Link
                to="/work/$slug"
                params={{ slug: p.slug }}
                onMouseEnter={() => setHover(i)}
                onMouseLeave={() => setHover((h) => (h === i ? null : h))}
                onFocus={() => setHover(i)}
                onBlur={() => setHover(null)}
                className="group flex items-baseline justify-between gap-6 py-5 md:py-7"
              >
                <span className="flex items-baseline gap-5">
                  <span className="font-mono text-[9px] uppercase tracking-[0.28em] text-red">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span
                    className={`font-sans text-[clamp(1.6rem,5.4vw,4rem)] font-light uppercase leading-[0.95] tracking-[-0.01em] transition-all duration-500 ${
                      hover === i ? "text-bone md:translate-x-3" : "text-bone/70"
                    }`}
                  >
                    {p.title}
                  </span>
                </span>
                <span className="shrink-0 font-mono text-[9px] uppercase tracking-[0.28em] text-bone/65">
                  {p.year}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      </main>

      <div className="relative z-10">
        <SiteFooter />
      </div>
    </div>
  );
}
